import { BillingEnv, Env } from '@@types/Api';
import { Subscription } from '@@types/Api/Common';
import { StreamPushSetting, StreamPushSettingValue } from '@@types/Api/Me';
import {
  OrganizationGeneralSetting,
  OrganizationProfileSetting,
  TenantCommonPreviewSetting,
  TenantGeneralSetting,
} from '@@types/Api/Setting';
import { AllPreference, MePreferenceMap } from '@@types/Preference';
import { account } from '@dooray/api-client';
import { CustomSettingStore } from '@dooray/repository-store';

import {
  CoachMarkType,
  MAX_MESSAGE_LEN,
  MAX_MESSAGE_LEN_LITE,
  NOTIFICATION,
  SERVICE,
  SUBSCRIPTION_PLAN,
} from '~/constants';
import { PreferenceCategory } from '~/constants/member';
import { api } from '~/stores/providers/Api2';
import logging from '~/stores/providers/logging';
import { userStore } from '~/stores/UserStore';
import { isNonEmpty } from '~/utils/fnUtil/fnUtil';
import { lift, mMaybe } from '~/utils/monadUtil';
import { first, get, isUndefined } from 'lodash';
import { action, computed, observable } from 'mobx';
import { __, filter, identity, includes, pipe, prop, tap } from 'ramda';
import propOr from 'ramda/es/propOr';

class GlobalSettingStore {
  @observable
  isLoadingUserPreferences = true;

  @observable
  isLoadingGeneralSetting = true;

  @observable
  isNewFeature;

  @observable
  userPreferences: Partial<MePreferenceMap> = {};

  @observable
  translator = { enabled: false };

  @observable
  organizationChart = { use: false };

  @observable
  organizationChartGeneral = {
    visibility: 'tenant',
    empty: false,
  };

  // account 변경을 최소화하기 위해서 내부 설정은 roundee로 가지고 있기로 함
  // https://nhnent.dooray.com/project/posts/2981838913281987651
  @observable
  systemCommands = { roundee: false, webex: false };

  @observable
  messengerGeneralSetting = {
    fileSizeLimit: 0,
    fileRetentionPeriodDays: 0,
    imageRetentionPeriodDays: 0,
    canDeleteMessage: false,
    messageDeletablePeriodMinutes: 5,
  };

  @observable
  tenantSetting = {
    conversionNoticeFlag: false,
    freeTrialFlag: false,
    firstExpiredAt: '',
    secondExpiredAt: '',
    toBeDeletedAt: '',
  };

  @observable
  isFetchingOrgChart = true;

  @observable
  subscription: Partial<Subscription> = {
    plan: SUBSCRIPTION_PLAN.LITE,
    services: [{ name: SERVICE.MESSENGER }],
    updatedAt: '',
    agreePayment: false,
  };

  @observable
  env: Env;

  envPromise = null;

  @observable
  aiProductItem: account.AvailableProductItem['option'];

  @observable
  billingEnv: Partial<BillingEnv> | null = null;

  @observable
  security = {
    canClipboardCopy: true,
    canClipboardPaste: true,
    canPrint: true,
  };

  tenantSettingPromise = null;
  ip = null;

  @action
  setSubscriptions = (subscription: Subscription) => {
    this.subscription = subscription;
  };

  @computed
  get isGov() {
    return this.env?.rawEnv === 'gov' || this.env?.rawEnv === 'govi';
  }

  @computed
  get isReal() {
    return this.env?.env === 'real';
  }

  @action
  setEnv = (env: Env) => {
    this.env = env;
  };

  @action
  setBillingEnv = (env: BillingEnv) => {
    this.billingEnv = env;
  };

  @computed
  get isLiteVersion(): boolean {
    return this.subscription.plan === SUBSCRIPTION_PLAN.LITE;
  }

  @computed
  get isBasicPlan() {
    return this.subscription.plan === SUBSCRIPTION_PLAN.BASIC;
  }

  @computed
  get isBusinessPlan() {
    return this.subscription.plan === SUBSCRIPTION_PLAN.BUSINESS;
  }

  @computed
  get isFreeTrial() {
    return this.tenantSetting.freeTrialFlag;
  }

  @computed
  get isDoorayNewsOn() {
    return this.streamPush;
  }

  get canShowProjectFeature() {
    return !isUndefined(this.env?.serviceUse?.project);
  }

  get canUseProject() {
    return this.env?.serviceUse?.project ?? false;
  }

  get canUseMail() {
    return this.env?.serviceUse?.mail ?? false;
  }

  get canUseCalendar() {
    return this.env?.serviceUse?.calendar ?? false;
  }

  get canUseForm() {
    return this.env?.subServiceUse?.form ?? false;
  }

  get logTextMaxLength() {
    return globalSettingStore.isLiteVersion ? MAX_MESSAGE_LEN_LITE : MAX_MESSAGE_LEN;
  }

  get enabledPreview() {
    return this.tenantCommonPreviewSetting?.value.enabled === true;
  }

  fetchSubscription = () => {
    return api.common.fetchSubscription().then(this.setSubscriptions);
  };

  fetchEnv = () => {
    return api.common
      .fetchServiceAcl()
      .then((acl) => {
        this.setEnv(acl);
        return acl;
      })
      .catch((err) => {
        logging.error('env 호출 에러', err);
      })
      .finally(() => {
        this.envPromise = null;
      });
  };

  cacheOrFetchEnv = () => {
    if (this.env) {
      return Promise.resolve(this.env);
    }

    if (this.envPromise) {
      return this.envPromise;
    }

    this.envPromise = this.fetchEnv();

    return this.envPromise;
  };

  @observable
  isInitializedCustomSetting = false;

  initCustomSettingStore = () => {
    return this.cacheOrFetchEnv().then(({ env }) => {
      return CustomSettingStore.initialize(env === 'ep').then(() => (this.isInitializedCustomSetting = true));
    });
  };

  canUseCustomFeature = (feature: account.CUSTOM_FEATURE) =>
    this.isInitializedCustomSetting ? CustomSettingStore.getInstance().canUseFeature(feature) : false;

  @action
  setTenantSetting = (tenantSetting) => {
    this.tenantSetting = tenantSetting;
  };

  @action
  setMessengerGeneralSetting = (setting) => {
    this.messengerGeneralSetting = setting;
  };

  @computed
  get canExportTaskFromMessenger() {
    return this.aiProductItem?.canExportTaskFromMessenger ?? false;
  }

  @action
  setAiProduct = (product) => {
    this.aiProductItem = product;
  };

  fetchAvailableAiProductItem = () => {
    return api.setting
      .fetchAvailableProductItems()
      .then(
        pipe(
          filter(pipe(prop('name'), includes(__, account.AI_PRODUCT_ITEM_NAMES))),
          first,
          prop('option'),
          this.setAiProduct
        )
      );
  };

  fetchMessengerGeneralSetting = () => {
    return api.setting.fetchMessengerGeneralSetting().then(prop('value')).then(this.setMessengerGeneralSetting);
  };

  fetchSystemCommands = () => {
    return api.setting.fetchSystemCommands().then(prop('value')).then(this.setSystemCommands);
  };

  fetchTranslatorSetting = () => {
    return api.setting.fetchTranslatorSetting().then(prop('value')).then(this.setTranslator);
  };

  fetchOrganizationChartSetting = (
    thenable: (setting: { use: boolean }) => Promise<unknown> = () => Promise.resolve()
  ) => {
    this.setIsFetchingOrgChart(true);
    api.setting
      .fetchOrganizationChartSetting()
      .then(prop('value'))
      .then(tap(this.setOrganizationChart))
      .then(thenable)
      .finally(() => this.setIsFetchingOrgChart(false));
  };

  fetchOrganizationChartGeneralSetting = () => {
    return api.setting
      .fetchOrganizationChartGeneralSetting()
      .then(prop('value'))
      .then(tap((value) => this.setOrganizationChartGeneral(value)));
  };

  @action
  setIsFetchingOrgChart = (value) => {
    this.isFetchingOrgChart = value;
  };

  @action
  setTranslator = (translatorSetting) => {
    this.translator = translatorSetting;
  };

  @action
  setSystemCommands = (systemCommands) => {
    this.systemCommands = systemCommands;
  };

  @action
  setMeetingEnabled = (value: boolean) => {
    this.systemCommands.roundee = value;
  };

  @action
  setWebexEnabled = (value: boolean) => {
    this.systemCommands.webex = value;
  };

  @action
  setOrganizationChart = (organizationChartSetting) => {
    this.organizationChart = organizationChartSetting;
  };

  @action
  setOrganizationChartGeneral = (organizationChartGeneralSetting) => {
    this.organizationChartGeneral = organizationChartGeneralSetting;
  };

  updateStreamPush = (useStreamChannel) => {
    return api.setting.updateStreamPush(useStreamChannel);
  };

  @computed
  get newLineManner() {
    return this.userPreferences[PreferenceCategory.NEW_LINE_MANNER]?.newLineManner;
  }

  @computed
  get notificationRange() {
    return this.userPreferences[PreferenceCategory.NOTIFICATION_RANGE]?.notificationRange;
  }

  @computed
  get lang() {
    return this.userPreferences[PreferenceCategory.LANG]?.lang;
  }

  @computed
  get notification(): NOTIFICATION {
    return this.userPreferences[PreferenceCategory.NOTIFICATION]?.notification;
  }

  @computed
  get forcePush(): boolean {
    return this.userPreferences[PreferenceCategory.FORCE_PUSH]?.forcePush;
  }

  @computed
  get clickToOpenThread(): boolean {
    return this.userPreferences[PreferenceCategory.THREAD]?.clickToOpen ?? true;
  }

  alwaysAlarm = false; // TODO 항상 알림 설정 API 반영

  @computed
  get canEditProfile() {
    return this.profileSetting?.value?.profileMember?.editable ?? true;
  }

  @computed
  get canEditProfileImage() {
    return this.profileSetting?.value?.profileImage?.editable ?? true;
  }

  @computed
  get canEditOfficeHours() {
    return this.profileSetting?.value?.officeHour?.editable ?? true;
  }

  @computed
  get canEditVacation() {
    return this.profileSetting?.value?.commonVacation?.editable ?? true;
  }

  @computed
  get canEditLanguageTimezone() {
    return this.profileSetting?.value?.languageTimezone?.editable ?? true;
  }

  @observable streamPush;

  @observable tabIndex;

  @observable guestGeneralSetting: OrganizationGeneralSetting | null = null;

  @observable profileSetting: OrganizationProfileSetting;

  @observable tenantGeneralSetting: TenantGeneralSetting;

  @observable tenantCommonPreviewSetting: TenantCommonPreviewSetting;

  @computed
  get hasProfileExternalUrl() {
    const { profileMember = {}, officeHour = {}, commonVacation = {} } = propOr({}, 'value', this.profileSetting);

    return (
      this.canShowExternalUrl(profileMember) ||
      this.canShowExternalUrl(officeHour) ||
      this.canShowExternalUrl(commonVacation)
    );
  }

  canShowExternalUrl = (settingGroup) => {
    return isNonEmpty(settingGroup.externalUrl) && !settingGroup.editable;
  };

  @action
  updatePreference = (preference: AllPreference) => {
    this.userPreferences[preference.category] = preference.value;
  };

  @action
  setUserPreferences = (preferences: AllPreference[]) => {
    this.userPreferences = preferences.reduce((acc: MePreferenceMap, { category, value }) => {
      acc[category] = value;
      return acc;
    }, {});
  };

  @action
  setDoorayNews = (stream: StreamPushSetting | StreamPushSettingValue) => {
    this.streamPush = get(stream, 'value.useStreamChannel', get(stream, 'useStreamChannel'));
  };

  @action
  setNewFeature = (newFeature) => {
    this.isNewFeature = newFeature;
  };

  @action
  setGuestGeneralSetting = (setting: OrganizationGeneralSetting) => {
    this.guestGeneralSetting = setting;
  };

  @action
  setProfileSetting = (setting: OrganizationProfileSetting) => {
    this.profileSetting = setting;
  };

  @action
  setTenantGeneralSetting = (setting) => {
    this.tenantGeneralSetting = setting;
  };

  @action
  setTenantCommonPreviewSetting = (setting) => {
    this.tenantCommonPreviewSetting = setting;
  };

  @action
  setLoadingGeneralSetting = (value: boolean) => {
    this.isLoadingGeneralSetting = value;
  };

  @action
  setLoadingUserPreferences = (value: boolean) => {
    this.isLoadingUserPreferences = value;
  };

  @action
  setSecurity = (security: { canClipboardCopy: boolean; canClipboardPaste: boolean; canPrint: boolean }) => {
    this.security = security;
  };

  fetchSecurityEnv = async () => {
    const securityEnv = await api.setting.fetchSecurityEnv();
    this.setSecurity(securityEnv.security.web);
  };

  handleIpChange = (ip: string) => {
    if (ip !== this.ip) {
      this.ip = ip;
      this.fetchSecurityEnv();
      this.cacheOrFetchEnv().then(() => {
        if (userStore.isOwner && this.env.adminServiceUse?.billing) {
          api.common.fetchBillingEnv().then(this.setBillingEnv);
        }
      });
    }
  };

  fetchOrganizationSettings = async (organizationId) => {
    if (!this.isInitializedCustomSetting) {
      await this.initCustomSettingStore();
    }

    const useGuestOrganization = this.canUseCustomFeature(account.CUSTOM_FEATURE.COMMON_GUEST_ORGANIZATION);

    this.setLoadingGeneralSetting(true);
    const [guestGeneralSetting, profileSetting] = await Promise.all([
      useGuestOrganization && api.setting.fetchGuestOrganizationGeneralSetting(),
      api.setting.fetchOrganizationProfileSetting(organizationId),
    ]);

    try {
      guestGeneralSetting && this.setGuestGeneralSetting(guestGeneralSetting);
      this.setProfileSetting(profileSetting);
    } finally {
      this.setLoadingGeneralSetting(false);
    }
  };

  loadTenantGeneralSetting = async () => {
    const result = await api.setting.fetchTenantGeneralSetting();
    this.setTenantGeneralSetting(result);
  };

  loadTenantCommonPreviewSetting = async () => {
    const result = await api.setting.fetchTenantCommonPreviewSetting();
    this.setTenantCommonPreviewSetting(result);
  };

  fetchDoorayNewsSetting = () => {
    api.me.fetchStreamPush().then(this.setDoorayNews);
  };

  @computed
  get coachMarkComplete(): {
    [CoachMarkType.Main]: boolean;
    [CoachMarkType.InviteChannel]: boolean;
    [CoachMarkType.StatusIcon]: boolean;
    [CoachMarkType.CloseThread]: boolean;
    [CoachMarkType.MakeThread]: boolean;
  } {
    return pipe<MePreferenceMap, any, any, any>(
      prop(PreferenceCategory.COACH_MARK),
      lift(identity),
      mMaybe(
        {
          [CoachMarkType.Main]: false,
          [CoachMarkType.InviteChannel]: false,
          [CoachMarkType.StatusIcon]: false,
          [CoachMarkType.CloseThread]: false,
          [CoachMarkType.MakeThread]: false,
        },
        identity
      )
    )(this.userPreferences);
  }

  @computed
  get isInviteChannelStepComplete(): boolean {
    return this.coachMarkComplete[CoachMarkType.InviteChannel];
  }

  @computed
  get isMakeThreadComplete(): boolean {
    return this.coachMarkComplete[CoachMarkType.MakeThread];
  }

  @computed
  get isCloseThreadComplete(): boolean {
    return this.coachMarkComplete[CoachMarkType.CloseThread];
  }

  constructor() {
    api.setIpHandler(this.handleIpChange);
  }
}

export default GlobalSettingStore;

export const globalSettingStore = new GlobalSettingStore();
